import React from 'react';
import { Link } from 'react-router';
import {
  ClapperboardIcon,
  LibraryBig,
  MailIcon,
  MessageCircleIcon,
  Music2Icon,
  NewspaperIcon,
  SparklesIcon,
} from 'lucide-react';
import { getCollectionById, type Widget } from '@airhead-games/shared/content';
import DocumentGradientCard, {
  type DocumentGradientCardIcon,
} from '@/content/rendering/documentListings/DocumentGradientCard';
import { cn } from '@/utilities/cn';
import type { PlayerColorFamily } from '@/styles/playerColors';

type LinkCardGridWidget = Extract<Widget, { type: 'linkCardGrid' }>;
type LinkCardGridItem = LinkCardGridWidget['items'][number];

interface WidgetLinkCardGridProps {
  widget: LinkCardGridWidget;
}

const CARD_ACCENTS: PlayerColorFamily[] = [
  'red',
  'orange',
  'yellow',
  'emerald',
  'sky',
  'indigo',
  'fuchsia',
];

const CARD_ACCENTS_BY_TITLE: Partial<Record<string, PlayerColorFamily>> = {
  Blog: 'sky',
  Clips: 'yellow',
  Discord: 'indigo',
  Email: 'fuchsia',
  Features: 'orange',
  Games: 'red',
  Music: 'emerald',
};

const ICONS_BY_TITLE: Record<string, DocumentGradientCardIcon> = {
  Blog: NewspaperIcon,
  Clips: ClapperboardIcon,
  Discord: MessageCircleIcon,
  Email: MailIcon,
  'Email Support': MailIcon,
  Features: SparklesIcon,
  Games: LibraryBig,
  Music: Music2Icon,
  "Poppy's Pals Discord": MessageCircleIcon,
};

const getItemHref = (item: LinkCardGridItem) => {
  if ('collectionId' in item) {
    return getCollectionById(item.collectionId)?.route;
  }

  return item.href;
};

const isHttpHref = (href: string) => /^https?:\/\//.test(href);
const isExternalHref = (href: string) =>
  isHttpHref(href) || href.startsWith('mailto:');

const getCardAccent = (
  item: LinkCardGridItem,
  index: number
): PlayerColorFamily =>
  CARD_ACCENTS_BY_TITLE[item.title] ??
  CARD_ACCENTS[index % CARD_ACCENTS.length];

const WidgetLinkCardGrid: React.FC<WidgetLinkCardGridProps> = ({ widget }) => (
  <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 xl:grid-cols-5">
    {widget.items.map((item, index) => {
      const href = getItemHref(item);
      if (!href) {
        return null;
      }

      const accent = getCardAccent(item, index);
      const Icon = ICONS_BY_TITLE[item.title] ?? SparklesIcon;
      const card = (
        <DocumentGradientCard
          title={item.title}
          Icon={Icon}
          accentFamily={accent}
          className="h-28 min-h-0 sm:h-32 lg:h-36 xl:h-40"
          titleClassName={cn(
            'text-xl sm:text-2xl lg:text-3xl 2xl:text-4xl',
            item.title.length > 8 &&
              'text-lg sm:text-xl lg:text-2xl 2xl:text-3xl'
          )}
        />
      );

      if (isExternalHref(href)) {
        return (
          <a
            key={`${item.title}-${href}`}
            href={href}
            aria-label={`Open ${item.title}`}
            target={isHttpHref(href) ? '_blank' : undefined}
            rel={isHttpHref(href) ? 'noreferrer' : undefined}
            className="group block rounded-xl focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            {card}
          </a>
        );
      }

      return (
        <Link
          key={`${item.title}-${href}`}
          to={href}
          aria-label={`Open ${item.title}`}
          className="group block rounded-xl focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          {card}
        </Link>
      );
    })}
  </div>
);

export default WidgetLinkCardGrid;
